import {useMemo} from 'react';

import {Typography} from '@/components/ui/typography';
import type {
  HealthLogMeasurement,
  HealthLogMetricDefinition,
} from '@/services/graphqlHooks';
import {
  formatHealthLogValuesForDisplay,
  type HealthLogDisplayValue,
} from '@/shared/residents/utils/healthLogUtils';

export type HealthLogValuesProps = {
  measurement: HealthLogMeasurement;
  metricDefinition?: HealthLogMetricDefinition;
};

const HealthLogValues = ({
  measurement,
  metricDefinition,
}: HealthLogValuesProps) => {
  const formattedValues = useMemo(
    () => formatHealthLogValuesForDisplay(measurement, metricDefinition),
    [measurement, metricDefinition]
  );

  const metricId = metricDefinition?.id ?? measurement.metricId;

  switch (metricId) {
    case 'bloodPressure':
      return <BloodPressureValues values={formattedValues} />;
    default:
      return <DefaultValues values={formattedValues} />;
  }
};

type ValuesProps = {
  values: HealthLogDisplayValue[];
};

const DefaultValues = ({values}: ValuesProps) => (
  <div className="flex flex-wrap gap-2">
    {values.map(({name, value, unit}, index) => (
      <Typography key={`${name}-${index}`}>
        {value}
        {unit ? ` ${unit}` : ''}
      </Typography>
    ))}
  </div>
);

const BloodPressureValues = ({values}: ValuesProps) => {
  const systolic = values.find(v => v.name.toLowerCase().includes('systolic'));
  const diastolic = values.find(v =>
    v.name.toLowerCase().includes('diastolic')
  );

  if (!systolic || !diastolic) {
    return <DefaultValues values={values} />;
  }

  const unit = systolic.unit || diastolic.unit || '';

  return (
    <Typography>
      <span>
        {systolic.value}/{diastolic.value}
      </span>
      {unit ? ` ${unit}` : ''}
    </Typography>
  );
};

export default HealthLogValues;
